import { z } from 'zod';
import {
  AncillaryDirectionSchema,
  AncillaryRequestSchema,
  SeatPreferenceChoiceSchema,
} from './views.js';

/**
 * Corpos e respostas das rotas públicas de ancilares (app-integration, chat JWT).
 * Spec §4.1 e §4.3. `challengeId` é gerado pelo front uma vez por modal (mesmo
 * idioma do cancelamento de voucher); a proteção contra envio duplo é a transição
 * de estado da sessão no modal-session, não este campo sozinho.
 */

const ChallengeIdSchema = z.string().min(1).max(200);

/** As observações da pessoa (plano de milhas etc.): vão ao legado no `mileageCode`, o campo
 *  livre que o ticket do time mostra. */
const NoteSchema = z.string().trim().min(1).max(500);

function uniqueBy<T>(key: (item: T) => string, path: string) {
  return (items: T[], ctx: z.RefinementCtx) => {
    const seen = new Set<string>();
    items.forEach((item, index) => {
      const k = key(item);
      if (seen.has(k)) {
        ctx.addIssue({ code: 'custom', path: [index, path], message: `duplicate ${path}` });
      }
      seen.add(k);
    });
  };
}

/**
 * Ignorado desde a #4107: o fluxo de assento e bagagem é determinístico e não tem conversa.
 * Continua aceito só porque os corpos são `strict` e o app e o BFF anteriores ainda o mandam
 * enquanto o deploy roda; sai no release seguinte.
 */
const LegacyConversationIdSchema = z.uuid().optional();

export const CreateAncillarySessionBodySchema = z
  .object({
    kind: z.enum(['seat_map', 'baggage_quote']),
    conversationId: LegacyConversationIdSchema,
  })
  .strict();
export type CreateAncillarySessionBody = z.infer<typeof CreateAncillarySessionBodySchema>;

export const CreateAncillarySessionResponseSchema = z
  .object({
    sessionId: z.string().min(1),
    status: z.literal('loading'),
    pollAfterMs: z.number().int().positive(),
  })
  .strict();
export type CreateAncillarySessionResponse = z.infer<typeof CreateAncillarySessionResponseSchema>;

/** kind = seat_map. No máximo 1 poltrona por trecho; trecho omitido = cliente não quis escolher. */
export const SubmitSeatMapBodySchema = z
  .object({
    challengeId: ChallengeIdSchema,
    selections: z
      .array(z.object({ legRef: z.string().min(1), seatToken: z.string().min(1) }).strict())
      .min(1)
      .max(8)
      .superRefine(uniqueBy((s) => s.legRef, 'legRef')),
  })
  .strict();
export type SubmitSeatMapBody = z.infer<typeof SubmitSeatMapBodySchema>;

/** kind = baggage_quote. O backend usa as ofertas 1..N do sentido para a quantidade pedida. */
export const SubmitBaggageBodySchema = z
  .object({
    challengeId: ChallengeIdSchema,
    bags: z
      .array(
        z
          .object({
            direction: AncillaryDirectionSchema,
            quantity: z.number().int().min(1).max(4),
            note: NoteSchema.optional(),
          })
          .strict(),
      )
      .min(1)
      .max(2)
      .superRefine(uniqueBy((b) => b.direction, 'direction')),
  })
  .strict();
export type SubmitBaggageBody = z.infer<typeof SubmitBaggageBodySchema>;

/**
 * `POST /vouchers/:voucherCode/ancillaries/baggage-request` — malas SEM cotação: vai ao legado
 * só com a quantidade e o legado cota à mão (o mesmo caminho que o app usa hoje). É o plano B
 * da bagagem, como a preferência é a do assento, e serve dois casos: a plataforma não ofereceu
 * nada para o sentido, ou a pessoa quer uma mala mais PESADA que a da oferta (a de 32 kg do
 * diálogo). `weightKg` é o peso por mala desse segundo caso e vai na nota para o atendente;
 * sem ele, o atendente cota a mala padrão da tarifa.
 */
export const BaggageRequestBodySchema = z
  .object({
    challengeId: ChallengeIdSchema,
    conversationId: LegacyConversationIdSchema,
    bags: z
      .array(
        z
          .object({
            direction: AncillaryDirectionSchema,
            quantity: z.number().int().min(1).max(4),
            weightKg: z.number().int().min(1).max(50).optional(),
            note: NoteSchema.optional(),
          })
          .strict(),
      )
      .min(1)
      .max(2)
      .superRefine(uniqueBy((b) => b.direction, 'direction')),
  })
  .strict();
export type BaggageRequestBody = z.infer<typeof BaggageRequestBodySchema>;

/** `POST /ancillaries/sessions/:sessionId/submit` — o formato válido depende do `kind` da sessão. */
export const SubmitAncillaryBodySchema = z.union([
  SubmitSeatMapBodySchema,
  SubmitBaggageBodySchema,
]);
export type SubmitAncillaryBody = z.infer<typeof SubmitAncillaryBodySchema>;

/**
 * `POST /vouchers/:voucherCode/ancillaries/seat-preference` — o pedido sem mapa, POR VOO
 * (#3939): o que a pessoa aceita em cada voo, o conforto e as observações. O legado recebe UM
 * pedido por sentido (`createTravelSeat`), e quem junta os voos do sentido é o modal-session.
 * Voo sem nada marcado não entra: sem marcação, não se pede assento no trecho.
 */
export const SeatPreferenceBodySchema = z
  .object({
    challengeId: ChallengeIdSchema,
    conversationId: LegacyConversationIdSchema,
    legs: z
      .array(
        z
          .object({ legRef: z.string().min(1), note: NoteSchema.optional() })
          .extend(SeatPreferenceChoiceSchema.shape)
          .strict()
          .refine(
            (leg) => leg.rows.length + leg.positions.length > 0 || leg.comfort,
            'a leg without any mark asks for nothing',
          ),
      )
      .min(1)
      .max(8)
      .superRefine(uniqueBy((l) => l.legRef, 'legRef')),
  })
  .strict();
export type SeatPreferenceBody = z.infer<typeof SeatPreferenceBodySchema>;

export const SubmitAncillaryResponseSchema = z
  .object({
    /** `handoff` = a mutation do legado falhou e o backend já abriu o card no Kanban. */
    status: z.enum(['submitted', 'handoff']),
    request: AncillaryRequestSchema,
  })
  .strict();
export type SubmitAncillaryResponse = z.infer<typeof SubmitAncillaryResponseSchema>;

/**
 * Corpos de erro (uma palavra), mapeados por `Record` exaustivo no BFF. Os 401
 * (`missing token` / `invalid token`) são do middleware do chat JWT e ficam fora.
 */
export const AncillaryErrorCodeSchema = z.enum([
  'invalid_body',
  'identity_not_found',
  'conversation_not_owned',
  'not_found',
  'not_eligible',
  'session_not_ready',
  'already_submitted',
  /** Já há um pedido aberto (ou confirmado, no assento) para este voo ou sentido. */
  'already_requested',
  'expired',
  'invalid_selection',
  'upstream_failed',
]);
export type AncillaryErrorCode = z.infer<typeof AncillaryErrorCodeSchema>;

export const ANCILLARY_ERROR_HTTP_STATUS = {
  invalid_body: 400,
  identity_not_found: 403,
  conversation_not_owned: 403,
  not_found: 404,
  not_eligible: 409,
  session_not_ready: 409,
  already_submitted: 409,
  already_requested: 409,
  expired: 409,
  invalid_selection: 422,
  upstream_failed: 502,
} as const satisfies Record<AncillaryErrorCode, number>;
